import { ClientPortfolioPaymentFrequencyEnum } from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import {
	optionalDateAsIsoString,
	optionalPositiveFloat,
	refineDateOrder,
	trimmedNullable,
} from "web_myplann/Shared/Validation/zodHelpers";
import { z } from "zod";

export const getBuildingSavingsFormSchema = (t: TranslateFunction) => {
	const maxMessage = t(Resources.Portfolio.AddShared.Validations.maxValue);
	const positiveNumberMessages = {
		positiveMessage: t(
			Resources.Portfolio.AddShared.Validations.positiveNumber,
		),
		maxMessage,
	};

	return z
		.object({
			name: trimmedNullable(
				z
					.string()
					.min(1, t(Resources.Portfolio.AddShared.Validations.required))
					.max(
						ValidationLimits.TextLength.LongName,
						t(Resources.Portfolio.AddShared.Validations.maxLength, {
							max: ValidationLimits.TextLength.LongName,
						}),
					),
			),
			contractNumber: trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.Name,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.Name,
					}),
				),
			),
			producer: trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.LongName,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.LongName,
					}),
				),
			),
			product: trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.LongName,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.LongName,
					}),
				),
			),
			productCategoryId: z.number().nullable(),
			signedAt: optionalDateAsIsoString(),
			startAt: optionalDateAsIsoString(),
			endAt: optionalDateAsIsoString(),
			payment: optionalPositiveFloat(
				ValidationLimits.Amount.Max,
				positiveNumberMessages,
			),
			paymentFrequency: z.nativeEnum(ClientPortfolioPaymentFrequencyEnum),
			appreciation: z
				.number()
				.gt(0, t(Resources.Portfolio.AddShared.Validations.positiveNumber))
				.nullish()
				.transform((val) => val ?? null),
			stateContribution: z.enum(["Yes", "No"]).nullable(),
			targetAmount: optionalPositiveFloat(
				ValidationLimits.Amount.Max,
				positiveNumberMessages,
			),
			note: trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.Note,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.Note,
					}),
				),
			),
		})
		.superRefine(
			refineDateOrder({
				startBeforeSigned: t(
					Resources.Portfolio.AddShared.Validations.startBeforeSigned,
				),
				endBeforeSigned: t(
					Resources.Portfolio.AddShared.Validations.endBeforeSigned,
				),
				endBeforeStart: t(
					Resources.Portfolio.AddShared.Validations.endBeforeStart,
				),
			}),
		);
};

export type BuildingSavingsFormInput = z.input<
	ReturnType<typeof getBuildingSavingsFormSchema>
>;
export type BuildingSavingsFormOutput = z.output<
	ReturnType<typeof getBuildingSavingsFormSchema>
>;
